import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { DatePicker } from 'antd'
import { Icon } from '@iconify/react'
import dayjs from 'dayjs'

import { isMongoObjectId } from '../../../storyMap.service'
import { loginState } from '../../../../login/login.slice'
import {
    getStoryStatusLabel,
    normalizeHexColor,
    normalizeStoryStatus,
} from '../utils'
import { toQuillHtml } from './StoryMapDescriptionEditor'
import { toCommentHtml } from './toCommentHtml'
import {
    getStoryMapActivityCache,
    mergeActivityWithLocal,
    resolveActivityAuthorLabel,
    setStoryMapActivityCache,
    subscribeStoryMapActivity,
} from './storyMapActivityLocal'
import styles from './StoryMapCardDetailModal.module.css'

const ACTIVITY_PAGE_SIZE = 20
const FOOTER_ACTIVITY_LIMIT = 10

const FIELD_LABELS = {
    name: 'Title',
    description: 'Description',
    color: 'Color',
    status: 'Status',
    start: 'Start date',
    end: 'End date',
    attachments: 'Attachment',
    stepid: 'Step',
    releaseid: 'Release',
    journeyid: 'Journey',
    index: 'Position',
}

const normalizeActivityField = (field) =>
    String(field || '').toLowerCase().replace(/_/g, '')

const getFieldLabel = (field) => {
    const key = normalizeActivityField(field)
    return FIELD_LABELS[key] || field
}

const isStepField = (field) => normalizeActivityField(field) === 'stepid'
const isIndexField = (field) => normalizeActivityField(field) === 'index'
const isReleaseField = (field) => normalizeActivityField(field) === 'releaseid'
const isJourneyField = (field) => normalizeActivityField(field) === 'journeyid'

const MODULE_LABELS = {
    story: 'Story',
    step: 'Step',
    steps: 'Step',
    journey: 'Journey',
    release: 'Release',
}

const stripHtml = (html = '') =>
    String(html)
        .replace(/<[^>]*>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()

const isEmptyActivityValue = (value) => {
    if (value === null || value === undefined) return true

    const str = String(value).trim()
    if (!str) return true

    const lower = str.toLowerCase()
    return lower === 'null' || lower === 'undefined' || str.toUpperCase() === 'N/A'
}

const normalizeActivityValue = (value) => {
    if (isEmptyActivityValue(value)) return ''
    return value
}

const truncateText = (text = '', maxLength = 160) => {
    const normalized = String(text).trim()
    if (normalized.length <= maxLength) return normalized
    return `${normalized.slice(0, maxLength).trim()}…`
}

const isCommentActivity = (item) => {
    const action = String(item?.action || '').toLowerCase()
    const field = String(item?.field || '').toLowerCase()
    const description = String(item?.description || '').toLowerCase()

    return (
        action === 'commented' ||
        action === 'comment' ||
        field === 'comment' ||
        description.includes('added a comment') ||
        description.includes('posted a comment') ||
        description.includes('updated a comment') ||
        description.includes('edited a comment') ||
        description.includes('deleted a comment')
    )
}

const isCommentDeletedActivity = (item) => {
    if (!isCommentActivity(item)) return false

    const action = String(item?.action || '').toLowerCase()
    const description = String(item?.description || '').toLowerCase()

    if (action === 'deleted') return true
    if (description.includes('deleted a comment')) return true

    const field = String(item?.field || '').toLowerCase()
    if (
        field === 'comment' &&
        !isEmptyActivityValue(item?.oldValue) &&
        isEmptyActivityValue(item?.newValue)
    ) {
        return true
    }

    return false
}

const isCommentUpdatedActivity = (item) => {
    if (!isCommentActivity(item) || isCommentDeletedActivity(item)) return false

    const description = String(item?.description || '').toLowerCase()
    if (
        description.includes('updated a comment') ||
        description.includes('edited a comment')
    ) {
        return true
    }

    const field = String(item?.field || '').toLowerCase()
    return (
        field === 'comment' &&
        !isEmptyActivityValue(item?.oldValue) &&
        !isEmptyActivityValue(item?.newValue)
    )
}

const hasDescriptionContent = (value) => {
    if (isEmptyActivityValue(value)) return false
    return Boolean(stripHtml(String(value)))
}

const DESCRIPTION_COLLAPSED_CHARS = 120
const DESCRIPTION_COLLAPSED_LINES = 3

const DescriptionHtmlValue = ({
    value,
    variant = 'new',
    emptyLabel = 'Empty',
    collapsedChars = DESCRIPTION_COLLAPSED_CHARS,
}) => {
    const [expanded, setExpanded] = useState(false)
    const normalized = normalizeActivityValue(value)

    if (!hasDescriptionContent(normalized)) {
        return <span className={styles.timelineDescriptionEmpty}>{emptyLabel}</span>
    }

    const plainText = stripHtml(String(normalized))
    const isLong = plainText.length > collapsedChars

    return (
        <div className={styles.timelineDescriptionWrap}>
            <div
                className={`${styles.timelineDescriptionHtml} ${
                    variant === 'old' ? styles.timelineDescriptionHtmlOld : styles.timelineDescriptionHtmlNew
                } ${isLong && !expanded ? styles.timelineDescriptionCollapsed : ''}`}
                style={
                    isLong && !expanded
                        ? { WebkitLineClamp: DESCRIPTION_COLLAPSED_LINES }
                        : undefined
                }
                dangerouslySetInnerHTML={{ __html: toQuillHtml(normalized) }}
            />
            {isLong ? (
                <button
                    type="button"
                    className={styles.timelineDescriptionToggle}
                    onClick={() => setExpanded((prev) => !prev)}
                >
                    {expanded ? 'Show less' : 'Load more'}
                </button>
            ) : null}
        </div>
    )
}

const DescriptionChangeBlock = ({ oldValue, newValue }) => {
    const hasOld = hasDescriptionContent(normalizeActivityValue(oldValue))

    return (
        <div className={styles.timelineDescriptionDiff}>
            <div className={styles.timelineDescriptionDiffSection}>
                <span className={styles.timelineDescriptionDiffLabel}>Before</span>
                <div
                    className={`${styles.timelineDescriptionDiffBox} ${
                        styles.timelineDescriptionDiffBoxOld
                    }`}
                >
                    {hasOld ? (
                        <DescriptionHtmlValue value={oldValue} variant="old" collapsedChars={80} />
                    ) : (
                        <span className={styles.timelineDescriptionEmpty}>Empty</span>
                    )}
                </div>
            </div>

            <div className={styles.timelineDescriptionDiffDivider} aria-hidden>
                <Icon icon="mdi:arrow-down" width={16} />
            </div>

            <div className={styles.timelineDescriptionDiffSection}>
                <span className={styles.timelineDescriptionDiffLabel}>After</span>
                <div
                    className={`${styles.timelineDescriptionDiffBox} ${
                        styles.timelineDescriptionDiffBoxNew
                    }`}
                >
                    <DescriptionHtmlValue value={newValue} variant="new" />
                </div>
            </div>
        </div>
    )
}

const getCommentRaw = (item) => {
    const preferOldValue = isCommentDeletedActivity(item)
    const candidates = preferOldValue
        ? [item?.oldValue, item?.content, item?.commentText, item?.newValue]
        : [item?.newValue, item?.content, item?.commentText, item?.oldValue]

    for (const candidate of candidates) {
        if (!isEmptyActivityValue(candidate)) {
            return String(candidate).trim()
        }
    }

    // Fallback: pull quoted HTML from description ("updated a comment: <p>…</p>")
    const description = String(item?.description || '')
    const match = description.match(
        /(?:added|posted|updated|edited|deleted)\s+a\s+comment:\s*([\s\S]+)$/i
    )
    if (match?.[1] && !isEmptyActivityValue(match[1])) {
        return match[1].trim()
    }

    return null
}

const isAttachmentRemovedActivity = (item) => {
    const field = normalizeActivityField(item?.field)
    if (field !== 'attachments') return false

    const description = String(item?.description || '').toLowerCase()
    if (description.includes('removed attachment') || description.includes('removed an attachment')) {
        return true
    }

    return Boolean(item?.oldAttachment) && !item?.newAttachment
}

const getActivityIcon = (item) => {
    if (isCommentDeletedActivity(item)) return 'mdi:comment-remove-outline'
    if (isCommentUpdatedActivity(item)) return 'mdi:comment-edit-outline'
    if (isCommentActivity(item)) return 'mdi:comment-text-outline'

    const action = String(item?.action || '').toLowerCase()
    if (action === 'created') return 'mdi:plus'
    if (action === 'deleted') return 'mdi:delete-outline'

    const field = normalizeActivityField(item?.field)
    if (field === 'status') return 'mdi:flag-outline'
    if (field === 'description') return 'mdi:text-box-outline'
    if (field === 'name') return 'mdi:format-title'
    if (field === 'color') return 'mdi:palette-outline'
    if (field === 'attachments') {
        return isAttachmentRemovedActivity(item)
            ? 'mdi:paperclip-off'
            : 'mdi:paperclip'
    }
    if (field === 'start' || field === 'end') return 'mdi:calendar-outline'
    if (field === 'stepid') return 'mdi:shoe-print'
    if (field === 'index') return 'mdi:sort-numeric-variant'
    if (field === 'releaseid') return 'mdi:rocket-launch-outline'
    if (field === 'journeyid') return 'mdi:map-marker-path'

    if (action === 'updated') return 'mdi:swap-horizontal'

    return 'mdi:history'
}

const getTimelineNodeClass = (item) => {
    if (isCommentDeletedActivity(item)) return styles.timelineNodeCommentDeleted
    if (isCommentUpdatedActivity(item)) return styles.timelineNodeCommentUpdated
    if (isCommentActivity(item)) return styles.timelineNodeComment

    const action = String(item?.action || '').toLowerCase()
    if (action === 'created') return styles.timelineNodeCreated
    if (action === 'deleted') return styles.timelineNodeDeleted

    const field = normalizeActivityField(item?.field)
    if (field === 'status') return styles.timelineNodeStatus
    if (field === 'description') return styles.timelineNodeDescription
    if (field === 'name') return styles.timelineNodeName
    if (field === 'color') return styles.timelineNodeColor
    if (field === 'attachments') {
        return isAttachmentRemovedActivity(item)
            ? styles.timelineNodeAttachmentRemoved
            : styles.timelineNodeAttachment
    }
    if (field === 'start' || field === 'end') return styles.timelineNodeDate
    if (field === 'stepid') return styles.timelineNodeStep
    if (field === 'index') return styles.timelineNodeReorder
    if (field === 'releaseid') return styles.timelineNodeRelease
    if (field === 'journeyid') return styles.timelineNodeJourney

    return styles.timelineNodeUpdated
}

const formatActivityValue = (field, value) => {
    if (isEmptyActivityValue(value)) return null

    if (field === 'description') {
        return truncateText(stripHtml(value))
    }

    if (field === 'status') {
        return getStoryStatusLabel(value)
    }

    if (field === 'color') {
        return normalizeHexColor(value) || String(value).trim()
    }

    if (field === 'start' || field === 'end') {
        const numeric = Number(value)
        if (!Number.isNaN(numeric) && numeric > 0) {
            const parsed = dayjs(numeric)
            return parsed.isValid() ? parsed.format('D MMM YYYY') : truncateText(String(value))
        }
    }

    return truncateText(String(value))
}

const StatusPill = ({ value, muted = false }) => {
    const statusKey = normalizeStoryStatus(value)
    const label = getStoryStatusLabel(value)

    return (
        <span
            className={`${styles.timelineStatusPill} ${
                styles[`timelineStatusPill_${statusKey}`] || ''
            } ${muted ? styles.timelineStatusPillMuted : ''}`}
        >
            {label}
        </span>
    )
}

const isDateActivityField = (field) => field === 'start' || field === 'end'

const isImageAttachment = (attachment) => {
    if (!attachment) return false
    if (String(attachment.fileType || '').toLowerCase() === 'image') return true
    return /\.(png|jpe?g|gif|svg|webp)$/i.test(String(attachment.fileName || ''))
}

const getAttachmentFileIcon = (attachment) => {
    const fileType = String(attachment?.fileType || '').toLowerCase()
    if (fileType === 'pdf') return 'mdi:file-pdf-box'
    if (fileType === 'csv') return 'mdi:file-delimited-outline'
    if (isImageAttachment(attachment)) return 'mdi:file-image-outline'
    return 'mdi:file-outline'
}

const ActivityAttachmentPreview = ({ attachment, removed = false }) => {
    if (!attachment) return null

    const isImage = isImageAttachment(attachment)
    const cardClassName = `${styles.activityAttachmentCard} ${
        isImage ? styles.activityAttachmentCardImage : styles.activityAttachmentCardFile
    } ${removed ? styles.activityAttachmentCardRemoved : ''}`

    const content = isImage && attachment.url ? (
        <>
            <span className={styles.activityAttachmentThumbWrap}>
                <img
                    src={attachment.url}
                    alt={attachment.fileName}
                    className={styles.activityAttachmentThumb}
                    loading="lazy"
                />
            </span>
            <span className={styles.activityAttachmentName}>{attachment.fileName}</span>
        </>
    ) : (
        <>
            <span className={styles.activityAttachmentFileIcon}>
                <Icon icon={getAttachmentFileIcon(attachment)} width={18} />
            </span>
            <span className={styles.activityAttachmentName}>{attachment.fileName}</span>
        </>
    )

    if (attachment.url) {
        return (
            <a
                href={attachment.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cardClassName}
                onClick={(event) => event.stopPropagation()}
            >
                {content}
            </a>
        )
    }

    return <div className={cardClassName}>{content}</div>
}

const DateChangeValue = ({ display, variant = 'new' }) => {
    const isEmpty = !display || display === 'Empty'

    if (isEmpty) {
        return <span className={styles.timelineDateEmpty}>Empty</span>
    }

    return (
        <span
            className={`${styles.timelineDateChip} ${
                variant === 'old' ? styles.timelineDateChipOld : styles.timelineDateChipNew
            }`}
        >
            <Icon
                icon="mdi:calendar-outline"
                width={14}
                className={styles.timelineDateIcon}
                aria-hidden
            />
            <span>{display}</span>
        </span>
    )
}

const ChangeRow = ({ oldDisplay, newDisplay, newValue, oldValue, field }) => {
    if (isDateActivityField(field)) {
        return (
            <div className={`${styles.timelineChangeRow} ${styles.timelineDateChangeRow}`}>
                <DateChangeValue display={oldDisplay || 'Empty'} variant="old" />
                <Icon icon="mdi:arrow-right" width={14} className={styles.timelineChangeArrow} />
                <DateChangeValue display={newDisplay || 'Empty'} variant="new" />
            </div>
        )
    }

    return (
        <div className={styles.timelineChangeRow}>
            <span className={styles.timelineChangeFrom}>{oldDisplay || 'Empty'}</span>
            <Icon icon="mdi:arrow-right" width={14} className={styles.timelineChangeArrow} />
            <span className={styles.timelineChangeTo}>
                {field === 'color' && newDisplay ? (
                    <>
                        <span
                            className={styles.timelineColorSwatch}
                            style={{
                                backgroundColor: normalizeHexColor(newValue) || '#e5e7eb',
                            }}
                            aria-hidden
                        />
                        {newDisplay}
                    </>
                ) : (
                    newDisplay || 'Empty'
                )}
            </span>
        </div>
    )
}

const ActivityHeadline = ({ item, currentUser }) => {
    const author = resolveActivityAuthorLabel(item, currentUser)
    const action = String(item.action || '').toLowerCase()
    const moduleLabel = MODULE_LABELS[item.moduleType] || 'card'

    if (isCommentActivity(item)) {
        if (isCommentDeletedActivity(item)) {
            return (
                <p className={styles.timelineHeadline}>
                    <span className={styles.timelineAuthor}>{author}</span>{' '}
                    deleted a{' '}
                    <strong className={styles.timelineHighlight}>comment</strong>
                </p>
            )
        }

        if (isCommentUpdatedActivity(item)) {
            return (
                <p className={styles.timelineHeadline}>
                    <span className={styles.timelineAuthor}>{author}</span>{' '}
                    updated a{' '}
                    <strong className={styles.timelineHighlight}>comment</strong>
                </p>
            )
        }

        return (
            <p className={styles.timelineHeadline}>
                <span className={styles.timelineAuthor}>{author}</span> added a{' '}
                <strong className={styles.timelineHighlight}>comment</strong>
            </p>
        )
    }

    if (action === 'created') {
        return (
            <p className={styles.timelineHeadline}>
                <span className={styles.timelineAuthor}>{author}</span> created this{' '}
                <strong className={styles.timelineHighlight}>{moduleLabel.toLowerCase()}</strong>
            </p>
        )
    }

    if (action === 'deleted') {
        return (
            <p className={styles.timelineHeadline}>
                <span className={styles.timelineAuthor}>{author}</span> deleted this{' '}
                <strong className={styles.timelineHighlight}>{moduleLabel.toLowerCase()}</strong>
            </p>
        )
    }

    if (action === 'updated' && item.field) {
        if (item.field === 'status') {
            return (
                <p className={styles.timelineHeadline}>
                    <span className={styles.timelineAuthor}>{author}</span> changed{' '}
                    <strong className={styles.timelineHighlight}>status</strong>
                </p>
            )
        }

        if (item.field === 'name') {
            return (
                <p className={styles.timelineHeadline}>
                    <span className={styles.timelineAuthor}>{author}</span> renamed this{' '}
                    <strong className={styles.timelineHighlight}>{moduleLabel.toLowerCase()}</strong>
                </p>
            )
        }

        if (item.field === 'start' || item.field === 'end') {
            const dateLabel = item.field === 'start' ? 'start date' : 'end date'
            return (
                <p className={styles.timelineHeadline}>
                    <span className={styles.timelineAuthor}>{author}</span> updated the{' '}
                    <strong className={styles.timelineHighlight}>{dateLabel}</strong>
                </p>
            )
        }

        if (item.field === 'attachments') {
            if (item.newAttachment) {
                return (
                    <p className={styles.timelineHeadline}>
                        <span className={styles.timelineAuthor}>{author}</span> added an{' '}
                        <strong className={styles.timelineHighlight}>attachment</strong>
                    </p>
                )
            }

            if (item.oldAttachment) {
                return (
                    <p className={styles.timelineHeadline}>
                        <span className={styles.timelineAuthor}>{author}</span> removed an{' '}
                        <strong className={styles.timelineHighlight}>attachment</strong>
                    </p>
                )
            }
        }

        if (isStepField(item.field)) {
            return (
                <p className={styles.timelineHeadline}>
                    <span className={styles.timelineAuthor}>{author}</span> moved this{' '}
                    <strong className={styles.timelineHighlight}>{moduleLabel.toLowerCase()}</strong>{' '}
                    to a different step
                </p>
            )
        }

        if (isIndexField(item.field)) {
            return (
                <p className={styles.timelineHeadline}>
                    <span className={styles.timelineAuthor}>{author}</span> changed the{' '}
                    <strong className={styles.timelineHighlight}>position</strong> of this{' '}
                    {moduleLabel.toLowerCase()}
                </p>
            )
        }

        if (isReleaseField(item.field)) {
            return (
                <p className={styles.timelineHeadline}>
                    <span className={styles.timelineAuthor}>{author}</span> moved this{' '}
                    <strong className={styles.timelineHighlight}>{moduleLabel.toLowerCase()}</strong>{' '}
                    to a different release
                </p>
            )
        }

        if (isJourneyField(item.field)) {
            return (
                <p className={styles.timelineHeadline}>
                    <span className={styles.timelineAuthor}>{author}</span> moved this{' '}
                    <strong className={styles.timelineHighlight}>{moduleLabel.toLowerCase()}</strong>{' '}
                    to a different journey
                </p>
            )
        }

        const fieldLabel = getFieldLabel(item.field)

        return (
            <p className={styles.timelineHeadline}>
                <span className={styles.timelineAuthor}>{author}</span> updated the{' '}
                <strong className={styles.timelineHighlight}>{fieldLabel.toLowerCase()}</strong>
            </p>
        )
    }

    const fallback = stripHtml(item.description) || 'Made a change'

    return (
        <p className={styles.timelineHeadline}>
            <span className={styles.timelineAuthor}>{author}</span>{' '}
            <span>{fallback}</span>
        </p>
    )
}

const CommentQuote = ({ html, deleted = false }) => (
    <div
        className={`${styles.timelineCommentQuote} ${
            deleted ? styles.timelineCommentQuoteDeleted : ''
        }`}
    >
        <span className={styles.timelineCommentQuoteMark} aria-hidden>
            &ldquo;
        </span>
        <div
            className={styles.timelineCommentQuoteBody}
            dangerouslySetInnerHTML={{ __html: html }}
        />
        <span className={styles.timelineCommentQuoteMark} aria-hidden>
            &rdquo;
        </span>
    </div>
)

const ActivityDetailBar = ({ item }) => {
    if (isCommentActivity(item)) {
        const isDeleted = isCommentDeletedActivity(item)
        const isUpdated = isCommentUpdatedActivity(item)

        if (isUpdated) {
            const oldHtml = !isEmptyActivityValue(item?.oldValue)
                ? toCommentHtml(String(item.oldValue).trim())
                : null
            const newHtml = !isEmptyActivityValue(item?.newValue)
                ? toCommentHtml(String(item.newValue).trim())
                : toCommentHtml(getCommentRaw(item) || '')

            if (!oldHtml && !newHtml) return null

            return (
                <div className={styles.timelineDetailBar}>
                    <div className={styles.timelineCommentUpdate}>
                        {oldHtml ? (
                            <CommentQuote html={oldHtml} deleted />
                        ) : null}
                        {oldHtml && newHtml ? (
                            <Icon
                                icon="mdi:arrow-right"
                                width={14}
                                className={styles.timelineChangeArrow}
                            />
                        ) : null}
                        {newHtml ? <CommentQuote html={newHtml} /> : null}
                    </div>
                </div>
            )
        }

        const commentRaw = getCommentRaw(item)
        if (!commentRaw) return null

        const commentHtml = toCommentHtml(commentRaw)
        if (!commentHtml) return null

        return (
            <div className={styles.timelineDetailBar}>
                <CommentQuote html={commentHtml} deleted={isDeleted} />
            </div>
        )
    }

    const action = String(item.action || '').toLowerCase()
    if (action !== 'updated' || !item.field) return null

    const oldDisplay = formatActivityValue(item.field, item.oldValue)
    const newDisplay = formatActivityValue(item.field, item.newValue)

    if (item.field === 'status') {
        return (
            <div className={styles.timelineDetailBar}>
                <div className={styles.timelineStatusChange}>
                    <StatusPill value={item.oldValue} muted />
                    <Icon icon="mdi:arrow-right" width={14} className={styles.timelineChangeArrow} />
                    <StatusPill value={item.newValue} />
                </div>
            </div>
        )
    }

    if (item.field === 'name') {
        return (
            <div className={styles.timelineDetailBar}>
                <ChangeRow
                    field={item.field}
                    oldDisplay={oldDisplay || 'Untitled'}
                    newDisplay={newDisplay || 'Untitled'}
                    newValue={item.newValue}
                    oldValue={item.oldValue}
                />
            </div>
        )
    }

    if (item.field === 'description') {
        if (!hasDescriptionContent(item.oldValue) && !hasDescriptionContent(item.newValue)) {
            return null
        }

        return (
            <div className={`${styles.timelineDetailBar} ${styles.timelineDetailBarDescription}`}>
                <DescriptionChangeBlock
                    oldValue={item.oldValue}
                    newValue={item.newValue}
                />
            </div>
        )
    }

    if (item.field === 'attachments') {
        const attachment = item.newAttachment || item.oldAttachment
        if (!attachment) return null

        return (
            <div
                className={`${styles.timelineDetailBar} ${styles.timelineDetailBarAttachment}`}
            >
                <ActivityAttachmentPreview
                    attachment={attachment}
                    removed={Boolean(item.oldAttachment) && !item.newAttachment}
                />
            </div>
        )
    }

    if (isDateActivityField(item.field)) {
        if (!oldDisplay && !newDisplay) return null

        return (
            <div className={`${styles.timelineDetailBar} ${styles.timelineDetailBarDate}`}>
                <ChangeRow
                    field={item.field}
                    oldDisplay={oldDisplay || 'Empty'}
                    newDisplay={newDisplay || 'Empty'}
                    newValue={item.newValue}
                    oldValue={item.oldValue}
                />
            </div>
        )
    }

    if (!oldDisplay && !newDisplay) return null

    return (
        <div className={styles.timelineDetailBar}>
            <ChangeRow
                field={item.field}
                oldDisplay={oldDisplay || 'Empty'}
                newDisplay={newDisplay || 'Empty'}
                newValue={item.newValue}
                oldValue={item.oldValue}
            />
        </div>
    )
}

const ActivityTimelineSkeleton = ({ rows = 5, compact = false }) => (
    <ul
        className={`${styles.timelineSkeleton} ${
            compact ? styles.timelineSkeletonCompact : ''
        }`}
        aria-busy="true"
        aria-label="Loading activity"
    >
        {Array.from({ length: rows }).map((_, index) => {
            const isLast = index === rows - 1
            const showDetail = index % 2 === 0

            return (
                <li
                    key={index}
                    className={`${styles.timelineSkeletonEntry} ${
                        isLast ? styles.timelineSkeletonEntryLast : ''
                    }`}
                >
                    <div className={styles.timelineDateCol}>
                        {index === 0 || index === 3 ? (
                            <span className={`${styles.skPulse} ${styles.skPulseDate}`} />
                        ) : null}
                    </div>
                    <div className={styles.timelineSkeletonTrack}>
                        <span className={`${styles.skPulse} ${styles.skPulseNode}`} />
                    </div>
                    <div className={styles.timelineSkeletonBody}>
                        <span
                            className={`${styles.skPulse} ${styles.skPulseHeadline}`}
                            style={{ width: `${58 + (index % 3) * 12}%` }}
                        />
                        <span className={`${styles.skPulse} ${styles.skPulseTime}`} />
                        {showDetail && !compact ? (
                            <span className={`${styles.skPulse} ${styles.skPulseDetail}`} />
                        ) : null}
                    </div>
                </li>
            )
        })}
    </ul>
)

const ActivityLoadingState = ({ compact = false }) => (
    <ActivityTimelineSkeleton rows={compact ? 3 : 5} compact={compact} />
)

const ActivityEmptyState = ({ title, description }) => (
    <div className={styles.activityEmpty}>
        <div className={styles.activityEmptyIconWrap}>
            <Icon icon="mdi:timeline-text-outline" width={24} />
        </div>
        <p className={styles.activityEmptyTitle}>{title}</p>
        {description ? <p className={styles.activityEmptyDesc}>{description}</p> : null}
    </div>
)

const ActivityTimelineItem = ({ item, showDate, currentUser }) => (
    <li className={styles.timelineEntry}>
        <div className={styles.timelineDateCol}>
            {showDate ? (
                <span className={styles.timelineDate}>{item.dateLabel}</span>
            ) : null}
        </div>

        <div className={styles.timelineTrack}>
            <span
                className={`${styles.timelineNode} ${getTimelineNodeClass(item)}`}
                aria-hidden
            >
                <Icon icon={getActivityIcon(item)} width={15} />
            </span>
        </div>

        <div className={styles.timelineContent}>
            <ActivityHeadline item={item} currentUser={currentUser} />
            {item.createdAt ? (
                <time
                    className={styles.timelineTimestamp}
                    dateTime={item.createdAt}
                >
                    {item.createdAt}
                </time>
            ) : null}
            <ActivityDetailBar item={item} />
        </div>
    </li>
)

const StoryMapActivityTab = ({
    cardId,
    onLoadActivity,
    isActive = true,
    open = true,
    variant = 'default',
}) => {
    const { userProfile } = useSelector(loginState)
    const isFooter = variant === 'footer'
    const showDateFilter = !isFooter
    const pageSize = isFooter ? FOOTER_ACTIVITY_LIMIT : ACTIVITY_PAGE_SIZE
    const [activities, setActivities] = useState([])
    const [loading, setLoading] = useState(false)
    const [loadingMore, setLoadingMore] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const [dateRange, setDateRange] = useState(null)
    const skipRef = useRef(0)
    const loadingMoreRef = useRef(false)
    const loadingRef = useRef(false)
    const hasMoreRef = useRef(false)
    const scrollRef = useRef(null)
    const sentinelRef = useRef(null)

    const dateFilterParams = useMemo(() => {
        if (!dateRange?.[0] || !dateRange?.[1]) return {}
        return {
            from: dateRange[0].startOf('day').format('YYYY-MM-DD'),
            to: dateRange[1].endOf('day').format('YYYY-MM-DD'),
        }
    }, [dateRange])

    const hasDateFilter = Boolean(dateFilterParams.from && dateFilterParams.to)

    useEffect(() => {
        hasMoreRef.current = hasMore
    }, [hasMore])

    useEffect(() => {
        loadingRef.current = loading
    }, [loading])

    const loadMore = useCallback(async () => {
        if (
            isFooter ||
            !cardId ||
            !onLoadActivity ||
            !open ||
            !isMongoObjectId(cardId) ||
            loadingMoreRef.current ||
            loadingRef.current ||
            !hasMoreRef.current
        ) {
            return
        }

        loadingMoreRef.current = true
        setLoadingMore(true)

        try {
            const result = await onLoadActivity(cardId, {
                skip: skipRef.current,
                limit: ACTIVITY_PAGE_SIZE,
                ...dateFilterParams,
            })

            if (result?.success) {
                const incoming = result.activities || []
                let nextActivities = []
                setActivities((prev) => {
                    const existingIds = new Set(prev.map((entry) => entry.id))
                    const merged = incoming.filter((entry) => !existingIds.has(entry.id))
                    nextActivities = merged.length > 0 ? [...prev, ...merged] : prev
                    return nextActivities
                })
                setHasMore(Boolean(result.isNext))
                skipRef.current += incoming.length
                if (!hasDateFilter) {
                    setStoryMapActivityCache(cardId, {
                        activities: nextActivities,
                        hasMore: Boolean(result.isNext),
                        skip: skipRef.current,
                    })
                }
            }
        } finally {
            loadingMoreRef.current = false
            setLoadingMore(false)
        }
    }, [cardId, onLoadActivity, open, isFooter, dateFilterParams, hasDateFilter])

    const loadInitial = useCallback(
        async (isCancelled) => {
            if (!cardId || !onLoadActivity || !open || !isMongoObjectId(cardId)) {
                setActivities([])
                setHasMore(false)
                return
            }

            const cached =
                !isFooter && !hasDateFilter
                    ? getStoryMapActivityCache(cardId)
                    : null
            const hasCache = Boolean(cached)

            if (hasCache) {
                setActivities(cached.activities ?? [])
                setHasMore(Boolean(cached.hasMore))
                skipRef.current = cached.skip ?? cached.activities?.length ?? 0
                setLoading(false)
            } else {
                setLoading(true)
                setActivities([])
                setHasMore(false)
                skipRef.current = 0
            }

            const result = await onLoadActivity(cardId, {
                skip: 0,
                limit: pageSize,
                ...dateFilterParams,
            })

            if (isCancelled?.()) return

            if (result?.success) {
                let nextActivities = hasDateFilter
                    ? result.activities || []
                    : mergeActivityWithLocal(cardId, result.activities || [])
                if (isFooter) {
                    nextActivities = nextActivities.slice(0, FOOTER_ACTIVITY_LIMIT)
                }
                setActivities(nextActivities)
                setHasMore(Boolean(result.isNext))
                skipRef.current = nextActivities.length
                if (!isFooter && !hasDateFilter) {
                    setStoryMapActivityCache(cardId, {
                        activities: nextActivities,
                        hasMore: Boolean(result.isNext),
                        skip: nextActivities.length,
                    })
                }
            }

            if (!isCancelled?.()) {
                setLoading(false)
            }
        },
        [
            cardId,
            onLoadActivity,
            open,
            isFooter,
            pageSize,
            dateFilterParams,
            hasDateFilter,
        ]
    )

    useEffect(() => {
        if (!open) {
            setActivities([])
            setLoading(false)
            setLoadingMore(false)
            setHasMore(false)
            setDateRange(null)
            skipRef.current = 0
            loadingMoreRef.current = false
        }
    }, [open])

    useEffect(() => {
        if (!cardId || !open || hasDateFilter) return undefined

        return subscribeStoryMapActivity(cardId, (nextActivities) => {
            setActivities(
                isFooter
                    ? (nextActivities || []).slice(0, FOOTER_ACTIVITY_LIMIT)
                    : nextActivities
            )
            setLoading(false)
        })
    }, [cardId, open, isFooter, hasDateFilter])

    useEffect(() => {
        if (!isActive || !open) return undefined

        let cancelled = false

        const run = async () => {
            await loadInitial(() => cancelled)
        }

        run()

        return () => {
            cancelled = true
        }
    }, [cardId, onLoadActivity, isActive, open, loadInitial])

    useEffect(() => {
        if (isFooter) return undefined

        const root = scrollRef.current
        const sentinel = sentinelRef.current

        if (!isActive || !open || !root || !sentinel || !hasMore || loading) return undefined

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting) {
                    loadMore()
                }
            },
            { root, rootMargin: '80px', threshold: 0 }
        )

        observer.observe(sentinel)

        return () => observer.disconnect()
    }, [isActive, open, hasMore, loading, activities.length, loadMore, isFooter])

    useEffect(() => {
        if (isFooter || !isActive || !open || loading || !hasMore) return undefined

        const root = scrollRef.current
        const sentinel = sentinelRef.current
        if (!root || !sentinel) return undefined

        const rootRect = root.getBoundingClientRect()
        const sentinelRect = sentinel.getBoundingClientRect()

        if (sentinelRect.top <= rootRect.bottom + 80) {
            loadMore()
        }

        return undefined
    }, [isActive, open, loading, hasMore, activities.length, loadMore, isFooter])

    if (!isMongoObjectId(cardId)) {
        return (
            <ActivityEmptyState
                title="No activity to show"
                description="Save this card first — updates will be tracked here."
            />
        )
    }

    const visibleActivities = isFooter
        ? activities.slice(0, FOOTER_ACTIVITY_LIMIT)
        : activities

    return (
        <div
            className={`${styles.activityTab} ${
                variant === 'footer'
                    ? styles.activityTabFooter
                    : variant === 'modal'
                      ? styles.activityTabModal
                      : ''
            }`}
        >
            {showDateFilter ? (
                <div className={styles.activityFilterBar}>
                    <div className={styles.activityFilterLabel}>
                        <Icon
                            icon="mdi:calendar-range"
                            width={15}
                            aria-hidden
                        />
                        <span>Date range</span>
                    </div>
                    <DatePicker.RangePicker
                        className={styles.activityDateRange}
                        popupClassName={styles.activityDateRangePopup}
                        size="middle"
                        value={dateRange}
                        onChange={(value) => setDateRange(value)}
                        allowClear
                        disabledDate={(current) =>
                            current && current.isAfter(dayjs().endOf('day'))
                        }
                        placeholder={['From', 'To']}
                        format="DD MMM YYYY"
                        separator={
                            <Icon
                                icon="mdi:arrow-right"
                                width={14}
                                className={styles.activityDateRangeSeparator}
                                aria-hidden
                            />
                        }
                        getPopupContainer={() => document.body}
                    />
                </div>
            ) : null}
            <div
                ref={scrollRef}
                className={`${styles.activityListScroll} ${
                    loading ? styles.activityListScrollLoading : ''
                }`}
            >
                {loading ? (
                    <ActivityLoadingState compact={isFooter} />
                ) : visibleActivities.length > 0 ? (
                    <ul className={styles.timelineList}>
                        {visibleActivities.map((item, index) => {
                            const previous = visibleActivities[index - 1]
                            const showDate =
                                item.dateKey &&
                                item.dateKey !== previous?.dateKey

                            return (
                                <ActivityTimelineItem
                                    key={item.id}
                                    item={item}
                                    showDate={showDate}
                                    currentUser={userProfile}
                                />
                            )
                        })}
                        {!isFooter && !loading && hasMore ? (
                            <li ref={sentinelRef} className={styles.activityLoadMore} aria-hidden>
                                {loadingMore ? (
                                    <span className={styles.activityLoadMoreSpinner}>
                                        <Icon
                                            icon="mdi:loading"
                                            className={styles.activityLoadingIcon}
                                            width={18}
                                        />
                                    </span>
                                ) : null}
                            </li>
                        ) : null}
                    </ul>
                ) : (
                    <ActivityEmptyState
                        title={
                            hasDateFilter
                                ? 'No activity in this range'
                                : 'No activity yet'
                        }
                        description={
                            hasDateFilter
                                ? 'Try a different date range to see more history.'
                                : 'Edits, status changes, and comments will show up in this timeline.'
                        }
                    />
                )}
            </div>
        </div>
    )
}

export default StoryMapActivityTab
